/**
 * Module dependencies.
 */

import { Controller, FieldValues, UseControllerProps } from 'react-hook-form';
import { Input, InputProps } from './input';
import React, { ForwardedRef, forwardRef } from 'react';

/**
 * `Props` type.
 */

type Props<T extends FieldValues> = UseControllerProps<T> &
  InputProps & {
    normalizeValue?: (newValue: string, value: string) => string;
  };

/**
 * `InputFieldComponent` component.
 */

export function InputFieldComponent<T extends FieldValues>(
  props: Props<T>,
  forwardedRef: ForwardedRef<any>
): JSX.Element {
  const { control, defaultValue, name, normalizeValue, rules, shouldUnregister, ...inputProps } = props;

  return (
    <Controller
      control={control}
      defaultValue={defaultValue}
      name={name}
      render={({ field: { name, onBlur, onChange, value }, fieldState: { error, invalid, isDirty, isTouched } }) => {
        return (
          <Input
            invalid={invalid}
            isDirty={isDirty}
            isTouched={isTouched}
            name={name}
            onBlur={onBlur as any}
            value={value}
            {...inputProps}
            error={error}
            onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              const newValue = event.target.value;
              const finalValue = normalizeValue ? normalizeValue(newValue, value) : newValue;

              onChange(finalValue);

              if (inputProps.onChange) {
                (inputProps.onChange as (value: any) => void)(finalValue);
              }
            }}
            ref={forwardedRef}
          />
        );
      }}
      rules={rules}
      shouldUnregister={shouldUnregister}
    />
  );
}

/**
 * Export `InputField`.
 */

export const InputField = forwardRef<Props<any>, any>(InputFieldComponent as any);
